import VideoPlug from '../app/common/VideoPlug'
import Logger from '../app/common/Logger'

let instanceID = 0
const logger = new Logger('MediaWorker')

export class MediaWorker {
    constructor(localVideoSelector, remoteVideoSelector, videoPlugText) {
        instanceID++

        this.instanceID = instanceID
        this.localVideoSelector = localVideoSelector
        this.remoteVideoSelector = remoteVideoSelector
        this.videoPlugText = videoPlugText

        this.localStream = null
        this.remoteStream = null
        this.videoPlug = null

        this.muted = false
        this.isHold = false
        this.volume = 100

        logger.info(`MediaWorker instance created with ID: ${this.instanceID}`)
    }

    /**
     * Получаем локальный медиа стрим
     * @returns {MediaStream|null} локальный медиа стрим
     */
    getLocalStream() {
        logger.info(`Getting local stream for instance: ${this.instanceID}`)
        return this.localStream
    }

    /**
     * Получаем внешний медиа стрим
     * @returns {MediaStream|null} внешний медиа стрим
     */
    getRemoteStream() {
        logger.info(`Getting remote stream for instance: ${this.instanceID}`)
        return this.remoteStream
    }

    /**
     * Устанавливаем локальный медиа стрим
     * @param {MediaStream|null} stream - медиа стрим
     */
    setLocalStream(stream) {
        logger.info(`Setting local stream for instance: ${this.instanceID}`)

        const localVideo = document.querySelector(this.localVideoSelector)
        if (!localVideo) {
            logger.error(new Error('Local video element not found'), 'Local video element missing')
            //return
        }

        if (stream) {
            // для современных браузеров
            if ('srcObject' in localVideo) {
                localVideo.srcObject = stream
            } else {
                // поддержка старых браузеров
                localVideo.src = window.URL.createObjectURL(stream)
            }

            this.localStream = stream

            // автоплей
            localVideo.onloadedmetadata = () => {
                localVideo.play().catch(e => {
                    logger.error(e, 'Failed to play local video')
                })
            }

            // удаляем свойство transform
            if (this.videoPlug) {
                localVideo.style.transform = 'none'
            }
        } else {
            // удаляем стрим из видео элемента
            localVideo.src = ''

            // останавливаем локальный стрим
            if (this.localStream) {
                this.stopStream(this.localStream)
            }

            this.localStream = null
            localVideo.style.removeProperty('transform')
        }
    }

    /**
     * Устанавливаем внешний медиа стрим
     * @param {MediaStream|null} stream - медиа стрим
     */
    setRemoteStream(stream) {
        logger.info(`Setting remote stream for instance: ${this.instanceID}`)

        const remoteVideo = document.querySelector(this.remoteVideoSelector)
        if (!remoteVideo) {
            logger.error(new Error('Remote video element not found'), 'Remote video element missing')
            //return
        }

        if (stream) {
            // для современных браузеров
            if ('srcObject' in remoteVideo) {
                remoteVideo.srcObject = stream
            } else {
                // поддержка старых браузеров
                remoteVideo.src = window.URL.createObjectURL(stream)
            }

            this.remoteStream = stream

            // автоплей
            remoteVideo.onloadedmetadata = () => {
                remoteVideo.play().catch(e => {
                    logger.error(e, 'Failed to play remote video')
                })
            }
        } else {
            remoteVideo.src = ''
            this.remoteStream = null
        }
    }

    /**
     * Обновляем стримы
     */
    updateStreams() {
        logger.info(`Updating streams for instance: ${this.instanceID}`)

        setTimeout(() => {
            this.setLocalStream(this.localStream)
            this.setRemoteStream(this.remoteStream)
        }, 0)
    }

    /**
     * Создаем локальный стрим
     * @param {Object} options - опции (audio, video)
     * @param {Boolean|Object} options.audio - свойсва аудио
     * @param {Boolean|Object} options.video - свойсва видео
     * @returns {Promise<Array>}
     */
    async startLocal(options) {
        logger.info(`Starting local media capture for instance: ${this.instanceID}`)

        try {
            let stream

            // Получаем аудио стрим
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: options.audio
                })
            } catch (e) {
                logger.error(e, 'Failed to get audio stream')
                throw e
            }

            // Получаем аудио стрим, при наличии опции
            if (options.video) {
                let videoStream
                try {
                    videoStream = await navigator.mediaDevices.getUserMedia({
                        video: options.video
                    })
                } catch (e) {
                    logger.info('Camera access failed, using video plug')
                    // В случаи ошибки создаем заглушку для видео
                    this.videoPlug = new VideoPlug({ text: this.videoPlugText })
                    videoStream = this.videoPlug.start()
                }

                // Объединяем аудио и видео треки в один стрим
                stream = new MediaStream([
                    ...stream.getTracks(),
                    ...videoStream.getTracks()
                ])
            }

            // Добавляем стоп метод для стрима, если нет
            if (stream && !stream.stop && stream.getTracks) {
                stream.stop = function() {
                    this.getTracks().forEach(track => track.stop())
                }
            }

            this.setLocalStream(stream)
            logger.info(`Local media capture started successfully for instance: ${this.instanceID}`)
            return []

        } catch (e) {
            logger.error(e, 'Failed to start local media capture')
            throw e
        }
    }

    /**
     * Остановка всех медиа стримов и чистим свойства
     */
    stop() {
        logger.info(`Stopping MediaWorker instance: ${this.instanceID}`)

        this.setLocalStream(null)
        this.setRemoteStream(null)

        if (this.videoPlug) {
            this.videoPlug.stop()
            this.videoPlug = null
        }
    }

    /**
     * Ставим/снимаем мьют
     * @returns {Boolean} состояние мьюта
     */
    mute() {
        logger.info(`Toggling mute for instance: ${this.instanceID}`)

        if (this.localStream && this.localStream.getAudioTracks) {
            const audioTracks = this.localStream.getAudioTracks()
            if (audioTracks.length > 0) {
                const currentEnabled = audioTracks[0].enabled
                audioTracks[0].enabled = !currentEnabled
                this.muted = currentEnabled
            } else {
                logger.error(new Error('No audio tracks found'), 'Audio tracks missing in local stream')
            }
        } else {
            logger.error(new Error('Local stream unavailable'), 'Cannot mute - local stream not available')
        }

        return this.muted
    }

    /**
     * Ставим/снимаем холд
     * @param {Boolean} value - значение (true = hold, false = resume)
     * @param {Boolean} isVideo - флажок для видео
     */
    hold(value, isVideo = false) {
        logger.info(`Setting hold state to ${value} for instance: ${this.instanceID}`)

        try {
            if (this.localStream) {
                const audioTracks = this.localStream.getAudioTracks()
                if (audioTracks.length > 0 && !this.muted) {
                    audioTracks[0].enabled = !value
                }

                if (isVideo) {
                    const videoTracks = this.localStream.getVideoTracks()
                    if (videoTracks.length > 0) {
                        videoTracks[0].enabled = !value
                    }
                }
            }

            if (this.remoteStream) {
                const audioTracks = this.remoteStream.getAudioTracks()
                if (audioTracks.length > 0) {
                    audioTracks[0].enabled = !value
                }

                if (isVideo) {
                    const videoTracks = this.remoteStream.getVideoTracks()
                    if (videoTracks.length > 0) {
                        videoTracks[0].enabled = !value
                    }
                }
            }

            this.isHold = value
        } catch (e) {
            logger.error(e, 'Failed to set hold state')
        }
    }

    /**
     * Получаем текущие состояние холда
     * @returns {Boolean} состояние холда
     */
    getIsHold() {
        return this.isHold
    }

    /**
     * Обработка эвента изменения громкости
     * @param {Number} data - уровень (0-100)
     */
    changeVolumeEvent(data) {
        logger.info(`Changing volume to ${data} for instance: ${this.instanceID}`)

        this.volume = data
        const volumeLevel = data / 100

        const localVideo = document.querySelector(this.localVideoSelector)
        const remoteVideo = document.querySelector(this.remoteVideoSelector)

        if (localVideo) {
            localVideo.volume = volumeLevel
        }

        if (remoteVideo) {
            remoteVideo.volume = volumeLevel
        }
    }

    /**
     * Остановка всех треков стрима
     * @param {MediaStream} stream - стрим
     * @private
     */
    stopStream(stream) {
        if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => {
                track.stop()
            })
        }
    }
}